import { Injectable, inject } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { createEffect } from '@ngrx/effects';
import { filter, tap } from 'rxjs';

import { ErrorService } from '@ninety/platform/services/services/error.service';
import { SessionStorageService } from '@ninety/platform/services/services/storage.service';
import { WINDOW } from '@ninety/platform/services/services/window.token';

const ERROR = 'ChunkLoadError';
const EXPIRY_KEY = 'chunkLoadRetryExpiry';
const COUNT_KEY = 'chunkLoadRetryCount';
const MSG = 'Please hard-reload the page.';
const RETRY_WINDOW_MS = 60 * 60 * 1000;

export function isChunkLoadFailure(error: unknown): boolean {
  // Zone.js may wrap promise rejections in { rejection, promise, zone } instead of a raw Error.
  // Unwrap to get the actual error so the message/name checks below can match.
  const unwrapped = (error as { rejection?: unknown })?.rejection ?? error;
  if (!unwrapped) return false;

  const name = unwrapped instanceof Error ? unwrapped.name : (unwrapped as { name?: string })?.name;
  const message =
    unwrapped instanceof Error ? unwrapped.message : String((unwrapped as { message?: string })?.message ?? unwrapped);

  const isChunkLoadError = name === ERROR;
  const isDynamicImportError =
    message.includes('Failed to fetch dynamically imported module') || // Chrome
    message.includes('Importing a module script failed') || // Safari
    message.includes('error loading dynamically imported module'); // Firefox

  return isChunkLoadError || isDynamicImportError;
}

/**
 * Retries a navigation that failed because a lazy chunk could not be loaded: first a
 * navigation, then a reload, then a toast. The budget is per tab and bounded to an hour.
 *
 * Reads router.events rather than ngrx's ROUTER_ERROR, which rebuilds the event with the
 * error stringified — that drops the zone.js rejection wrapper and leaves no `name` to match.
 */
@Injectable()
export class ChunkLoadRetryEffects {
  private readonly router = inject(Router);
  private readonly storage = inject(SessionStorageService);
  private readonly errorService = inject(ErrorService);
  private readonly window = inject(WINDOW);

  private handled = false;

  chunkLoadError$ = createEffect(
    () =>
      this.router.events.pipe(
        filter((evt): evt is NavigationError => evt instanceof NavigationError),
        filter(evt => isChunkLoadFailure(evt.error)),
        tap(evt => this.retryOrNotify(evt))
      ),
    { dispatch: false }
  );

  private retryOrNotify(evt: NavigationError): void {
    // One attempt per document. A failing page load can raise several chunk errors,
    // and spending the whole budget on one of them leaves nothing for the next load.
    if (this.handled) return;
    this.handled = true;

    const now = Date.now();
    const expiry = Number(this.storage.get(EXPIRY_KEY)) || 0;
    // An expiry beyond a full window came from a skewed clock, so treat it as absent.
    const withinWindow = expiry > now && expiry <= now + RETRY_WINDOW_MS;

    const stored = parseInt(this.storage.get(COUNT_KEY) || '0', 10);
    const count = withinWindow && Number.isFinite(stored) ? stored : 0;

    if (!withinWindow) this.storage.set(EXPIRY_KEY, String(now + RETRY_WINDOW_MS));
    this.storage.set(COUNT_KEY, String(count + 1));

    if (count < 1) this.window.location.assign(evt.url);
    else if (count < 2) this.window.location.reload();
    else this.errorService.notify(evt.error?.rejection ?? evt.error, MSG, ERROR);
  }
}
